import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

// Register English locale for country name lookups
countries.registerLocale(enLocale);

/**
 * Maps full country names to ISO 3166-1 alpha-2 country codes (lowercase)
 * for use with react-circle-flags library
 *
 * This manual mapping handles special cases, regions, and naming variations
 * that i18n-iso-countries doesn't recognize automatically.
 */
export const countryNameToIsoCode: Record<string, string> = {
  'N/A': '',
  Afghanistan: 'af',
  USA: 'us',
  Algeria: 'dz',
  Argentina: 'ar',
  Australia: 'au',
  'Australia/New Zealand': 'au', // Using Australia flag for combined region
  Austria: 'at',
  Bahamas: 'bs',
  Belgium: 'be',
  'Belgium and Luxembourg': 'be', // Using Belgium flag for combined region
  'Bosnia (Albania, Macedonia, Montenegro)': 'ba',
  Brazil: 'br',
  Bulgaria: 'bg',
  Canada: 'ca',
  'Canada (National,CADTH)': 'ca',
  'Canada (Quebec province,INESSS)': 'ca',
  'Central Asia & CIS (Kyrgystan, Mongolia, Tajikistan, Turkmenistan, Uzbekistan)': 'kg', // Using Kyrgyzstan flag for region
  Chile: 'cl',
  China: 'cn',
  Colombia: 'co',
  'Costa Rica': 'cr',
  Croatia: 'hr',
  Cyprus: 'cy',
  'Czech Republic': 'cz',
  Denmark: 'dk',
  'Dominican Republic': 'do',
  Ecuador: 'ec',
  Egypt: 'eg',
  'El Salvador': 'sv',
  'England and Wales': 'gb-eng', // England flag
  Estonia: 'ee',
  Finland: 'fi',
  France: 'fr',
  'French West Africa': 'fr', // Using France flag for region
  Germany: 'de',
  Greece: 'gr',
  Guatemala: 'gt',
  'GULF (UAE, Kuwait, Oman, Qatar & Bahrain)': 'ae', // Using UAE flag for Gulf region
  Honduras: 'hn',
  'Hong Kong': 'hk',
  Hungary: 'hu',
  India: 'in',
  Indonesia: 'id',
  'IRAN YEMEN SUDAN & SYRIA': 'ir', // Using Iran flag for region
  Iraq: 'iq',
  'Ireland, Republic of': 'ie',
  Ireland: 'ie',
  Israel: 'il',
  Italy: 'it',
  Jamaica: 'jm',
  Japan: 'jp',
  Jordan: 'jo',
  Kazakhstan: 'kz',
  'Korea, Republic of': 'kr',
  Korea: 'kr',
  Kuwait: 'kw',
  Latvia: 'lv',
  Lebanon: 'lb',
  'Levant (Jordan, Lebanon)': 'jo', // Using Jordan flag for Levant region
  Libya: 'ly',
  Lithuania: 'lt',
  Malaysia: 'my',
  'Malaysia and Brunei': 'my', // Using Malaysia flag for combined region
  Mali: 'ml',
  Malta: 'mt',
  Mexico: 'mx',
  Morocco: 'ma',
  Netherlands: 'nl',
  'New Zealand': 'nz',
  Nicaragua: 'ni',
  Norway: 'no',
  Panama: 'pa',
  Peru: 'pe',
  Philippines: 'ph',
  Poland: 'pl',
  Portugal: 'pt',
  'Puerto Rico': 'pr',
  Qatar: 'qa',
  Romania: 'ro',
  Russia: 'ru',
  'Saudi Arabia': 'sa',
  Scotland: 'gb-sct', // Scotland flag
  'Scotland (SMC)': 'gb-sct', // Scotland flag
  Serbia: 'rs',
  Singapore: 'sg',
  Slovakia: 'sk',
  Slovenia: 'si',
  'South Africa': 'za',
  Spain: 'es',
  Sweden: 'se',
  Switzerland: 'ch',
  Taiwan: 'tw',
  Thailand: 'th',
  'Trinidad & Tobago': 'tt',
  Tunisia: 'tn',
  Turkey: 'tr',
  Türkiye: 'tr',
  Ukraine: 'ua',
  'United Arab Emirates': 'ae',
  'United Kingdom': 'gb',
  Venezuela: 've',
  Vietnam: 'vn',
  Albania: 'al',
  Aruba: 'aw',
  Bahrain: 'bh',
  'Canada (National)': 'ca',
  'Canada (Quebec)': 'ca-qc', // Quebec flag available
  Oman: 'om',
  Other: '',
};

/**
 * Gets the ISO country code for a given country name
 * @param countryName - Full country name from countries.json
 * @returns Lowercase ISO 3166-1 alpha-2 code, or empty string if not found
 */
export function getCountryIsoCode(countryName: string): string {
  // First check manual mapping for special cases and regions
  if (countryNameToIsoCode[countryName]) {
    return countryNameToIsoCode[countryName];
  }

  // Try i18n-iso-countries automatic lookup
  const isoCode = countries.getAlpha2Code(countryName, 'en');
  if (isoCode) {
    return isoCode.toLowerCase();
  }

  // Return empty string if no match found
  return '';
}
